import React from 'react';

import AnswersFill from '@ad-scripts/presentation/components/common/icons/fill/answers-fill';
import ArrowRightStroke from '@ad-scripts/presentation/components/common/icons/stroke/arrow-right-stroke';
import ChatFill from '@ad-scripts/presentation/components/common/icons/fill/chat-fill';
import CommunicationsFill from '@ad-scripts/presentation/components/common/icons/fill/communications-fill';
import { Description } from 'src/presentation/components/pages/shared/FormPage';
import EmojiPickerFill from '@ad-scripts/presentation/components/common/icons/fill/emoji-picker-fill';
import {
  ExternalLink as ExternalLinkIcon,
  StyledCardIcon,
  StyledColumnDescription,
} from 'src/presentation/components/pages/home/elements';
import { getAgentDesktopURL } from 'src/presentation/components/pages/shared/utils/Utils';
import HelpFill from '@ad-scripts/presentation/components/common/icons/fill/help-fill';
import InboxFill from '@ad-scripts/presentation/components/common/icons/fill/inbox-fill';
import IvrMessageStroke from '@ad-scripts/presentation/components/common/icons/stroke/ivr-message-stroke';
import LinkOutStroke from '@ad-scripts/presentation/components/common/icons/stroke/link-out-stroke';
import ReportingFill from '@ad-scripts/presentation/components/common/icons/fill/reporting-fill';
import RouterLinkButton from 'src/presentation/components/RouterLinkButton';
import RoutingFill from '@ad-scripts/presentation/components/common/icons/fill/routing-fill';
import UserFillIcon from '@ad-scripts/presentation/components/common/icons/fill/user-fill';
import {
  StyledCard,
  StyledCardContainer,
  StyledCardDescription,
  StyledCardTitle,
  StyledColumn,
  StyledColumnLink,
  StyledColumnSecondaryBlock,
  StyledExternalLink,
  StyledGridContainer,
  StyledGridContainerMiddle,
  StyledH2,
} from './HomeLayouts';
import { CHAT_BRAND } from 'src/constants/branding';

const AdminContent = () => (
  <>
    <Description>Manage team, platform, and security settings for your organization.</Description>
    <StyledCardContainer>
      <StyledCard>
        <StyledCardIcon>
          <UserFillIcon />
        </StyledCardIcon>
        <StyledCardTitle>Manage your team</StyledCardTitle>
        <StyledCardDescription>Add your coworkers to Gladly and create teams for Reporting.</StyledCardDescription>
        <RouterLinkButton to="/users">Go to Users</RouterLinkButton>
      </StyledCard>
      <StyledCard>
        <StyledCardIcon>
          <CommunicationsFill />
        </StyledCardIcon>
        <StyledCardTitle>Configure Channels</StyledCardTitle>
        <StyledCardDescription>Set up your company’s preferred communication methods.</StyledCardDescription>
        <RouterLinkButton to="/email">Start now</RouterLinkButton>
      </StyledCard>
      <StyledCard>
        <StyledCardIcon>
          <RoutingFill />
        </StyledCardIcon>
        <StyledCardTitle>Automate your efforts</StyledCardTitle>
        <StyledCardDescription>Create rules to speed up your standard processes and procedures.</StyledCardDescription>
        <RouterLinkButton to="/rules">Set up Rules</RouterLinkButton>
      </StyledCard>
    </StyledCardContainer>
    <StyledH2>Level up your team</StyledH2>
    <StyledGridContainerMiddle>
      <StyledColumn>
        <InboxFill />
        <b>Set up Inboxes</b>
        <StyledColumnDescription>Create Inboxes to direct and manage incoming Conversations.</StyledColumnDescription>
        <StyledColumnLink to="/inboxes">
          Inboxes <ArrowRightStroke />
        </StyledColumnLink>
      </StyledColumn>
      <StyledColumn>
        <ReportingFill />
        <b>Assess your performance</b>
        <StyledColumnDescription>Track your team’s efforts and identify areas for improvement.</StyledColumnDescription>
        <StyledExternalLink href={getAgentDesktopURL('reports')}>
          Reports <LinkOutStroke />
        </StyledExternalLink>
      </StyledColumn>
      <StyledColumn>
        <ChatFill />
        <b>Interact with your customers</b>
        <StyledColumnDescription>
          Give your customers a way to chat with your team directly on your site.
        </StyledColumnDescription>
        <StyledColumnLink to="/chat">
          {CHAT_BRAND.name} <ArrowRightStroke />
        </StyledColumnLink>
      </StyledColumn>
      <StyledColumn>
        <IvrMessageStroke />
        <b>Route voice calls with ease</b>
        <StyledColumnDescription>Create an informative, branded voice experience with IVR.</StyledColumnDescription>
        <StyledColumnLink to="/ivrs">
          IVR <ArrowRightStroke />
        </StyledColumnLink>
      </StyledColumn>
    </StyledGridContainerMiddle>
    <StyledH2>Quick links</StyledH2>
    <StyledGridContainer>
      <StyledColumnSecondaryBlock>
        <StyledCardIcon>
          <EmojiPickerFill />
        </StyledCardIcon>
        <ExternalLinkIcon />
        <b>See what’s new</b>
        <StyledColumnDescription>
          Read the release notes to learn about new features and improvements.
        </StyledColumnDescription>
        <StyledExternalLink href="https://help.gladly.com/docs/release-notes">
          Release notes <LinkOutStroke />
        </StyledExternalLink>
      </StyledColumnSecondaryBlock>
      <StyledColumnSecondaryBlock>
        <StyledCardIcon>
          <HelpFill />
        </StyledCardIcon>
        <ExternalLinkIcon />
        <b>Your questions, answered</b>
        <StyledColumnDescription>
          Visit our Help Docs to troubleshoot product issues on your own.
        </StyledColumnDescription>
        <StyledExternalLink href="https://help.gladly.com">
          Help Docs <LinkOutStroke />
        </StyledExternalLink>
      </StyledColumnSecondaryBlock>
      <StyledColumnSecondaryBlock>
        <StyledCardIcon>
          <AnswersFill />
        </StyledCardIcon>
        <ExternalLinkIcon />
        <b>Learn new skills</b>
        <StyledColumnDescription>
          Enroll in Gladly Academy to read up on tutorials and best practices.
        </StyledColumnDescription>
        <StyledExternalLink href="https://academy.gladly.com">
          Gladly Academy <LinkOutStroke />
        </StyledExternalLink>
      </StyledColumnSecondaryBlock>
    </StyledGridContainer>
  </>
);

export default AdminContent;
